import React from 'react';
import KioskShell from '@/components/KioskShell';
import ErrorFallback from '@/components/ErrorFallback';

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level error boundary. Catches any unhandled React errors and renders
 * the ErrorFallback — a dark full-screen "Touch to restart" screen.
 * A white screen is NEVER shown.
 */
class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console in development; in production a real logger (Sentry, etc.) goes here
    console.error('[KioskErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

/**
 * Root application component.
 * Wraps the KioskShell in an ErrorBoundary.
 * Global CSS (fullscreen, no overflow) lives in styles/index.css.
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <KioskShell />
    </ErrorBoundary>
  );
};

export default App;
